import * as React from "react"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

type DatePickerProps = {
  value?: Date
  onChange: (date: Date | undefined) => void
  placeholder?: string
  className?: string
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = "Pick a date",
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  
  // Calculate default month to show (10 years ago)
  const defaultMonth = React.useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 30); // Show 30 years ago by default
    return date;
  }, []);

  const handleSelect = (date: Date | undefined) => {
    onChange(date);
    setOpen(false); // Close the popover after selection
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-[240px] justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          defaultMonth={defaultMonth}
          onSelect={handleSelect}
          initialFocus
          captionLayout="dropdown"
          fromYear={new Date().getFullYear() - 100}
          toYear={new Date().getFullYear()}
          disabled={(date) => {
            const today = new Date();
            const minDate = new Date();
            minDate.setFullYear(today.getFullYear() - 5);
            return date > today || date > minDate;
          }}
          classNames={{
            caption: "flex justify-center pt-2 pb-4 gap-2 items-center",
            caption_label: "hidden", // hide default label (we’re using dropdowns)
            dropdown: "w-[120px] rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            dropdown_month: "m-2 mb-3",
            dropdown_year: "m-2",
            nav: "flex items-center",
            nav_button: "h-6 w-6 bg-muted text-muted-foreground rounded-md hover:bg-accent hover:text-accent-foreground",
            table: "w-full border-collapse space-y-1",
            head_row: "flex",
            head_cell: "text-muted-foreground w-9 text-[0.8rem]",
            row: "flex w-full mt-1",
            cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent [&:has([aria-selected])]:text-accent-foreground hover:bg-muted",
            day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100",
            day_selected: "bg-primary text-primary-foreground hover:bg-primary/90",
            day_today: "bg-accent text-accent-foreground",
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

export default DatePicker
